class RebuyUpsell extends HTMLElement {
  constructor() {
    super();
    this.apiKey = this.dataset.apiKey;
    this.customDataSourceId = this.dataset.customDataSourceId;
    this.productId = this.dataset.productId;
    this.mode = this.dataset.mode || 'addon';
    this.titlePrefix = this.dataset.titlePrefix || 'Add a';
    this.titleSuffix = this.dataset.titleSuffix || 'for only';
    this.buttonText = this.dataset.buttonText || 'Add to Bag';
    this.showViewProduct = this.dataset.showViewProduct !== 'false';
    this.showDescription = this.dataset.showDescription !== 'false';
    this.loaded = false;
  }

  getApiEndpoint() {
    if (!this.apiKey || !this.productId) return null;
    return `https://rebuyengine.com/api/v1/custom/id/${this.customDataSourceId}?key=${this.apiKey}&shopify_product_ids=${this.productId}&limit=1`;
  }

  connectedCallback() {
    if (this.dataset.prerendered === 'true') {
      this.loaded = true;
      this.classList.add('rebuy-upsell--loaded');
      this.wireUpHandlers();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.loaded) {
            this.loaded = true;
            this.fetchRecommendations();
            observer.unobserve(this);
          }
        });
      },
      { rootMargin: '200px' }
    );

    observer.observe(this);
  }

  wireUpHandlers() {
    if (this.mode === 'upsell') {
      this.setupButtonHandler();
    } else {
      this.setupFormIntegration();
    }
  }

  async fetchRecommendations() {
    const apiEndpoint = this.getApiEndpoint();
    if (!apiEndpoint) {
      this.classList.add('rebuy-upsell--hidden');
      return;
    }

    try {
      const response = await fetch(apiEndpoint);
      if (!response.ok) throw new Error('Failed to fetch Rebuy recommendations');

      const data = await response.json();

      if (data.data && data.data.length > 0) {
        this.renderProduct(data.data[0]);
      } else {
        this.classList.add('rebuy-upsell--hidden');
      }
    } catch (error) {
      console.error('Rebuy Upsell Error:', error);
      this.classList.add('rebuy-upsell--hidden');
    }
  }

  renderProduct(product) {
    const variant = product.variants && product.variants[0];
    const price = variant ? variant.price : '0.00';
    const variantId = variant ? variant.id : null;
    const imageUrl = product.image?.src || product.images?.[0]?.src || '';
    const title = product.title || '';
    const handle = product.handle || '';
    const description = this.truncateText(product.description || '', 80);

    const viewProductLink = this.showViewProduct ? `
      <a href="/products/${handle}" class="rebuy-upsell__link">
        View Product
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
        </svg>
      </a>
    ` : '';

    const inlineAction = this.mode === 'upsell' ? `
      <button type="button" class="rebuy-upsell__button" data-variant-id="${variantId}">
        ${this.buttonText}
      </button>
    ` : viewProductLink;

    const checkboxElement = this.mode === 'addon' ? `
      <div class="rebuy-upsell__checkbox">
        <input type="checkbox"
          aria-label="Add ${title} to cart"
          data-variant-id="${variantId}"
          data-product-title="${title}">
      </div>
    ` : '';

    this.innerHTML = `
      <div class="rebuy-upsell__card">
        <a href="/products/${handle}" class="rebuy-upsell__image-wrapper">
          <div class="rebuy-upsell__blob">
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <path fill="#ffffff" d="M47.5,-57.2C59.9,-46.7,67.5,-30.5,71.1,-13.1C74.7,4.3,74.3,22.9,66.1,37.1C57.9,51.3,41.9,61.1,24.8,66.5C7.7,71.9,-10.5,72.9,-27.4,67.5C-44.3,62.1,-59.9,50.3,-68.2,34.6C-76.5,18.9,-77.5,-0.7,-71.8,-17.8C-66.1,-34.9,-53.7,-49.5,-39.3,-59.5C-24.9,-69.5,-8.5,-74.9,5.3,-81.1C19.1,-87.3,35.1,-67.7,47.5,-57.2Z" transform="translate(100 100)" />
            </svg>
          </div>
          ${imageUrl ? `<img src="${imageUrl}" alt="${title}" class="rebuy-upsell__image" loading="lazy" width="80" height="80">` : ''}
        </a>
        <div class="rebuy-upsell__content">
          <h4 class="rebuy-upsell__title">${this.titlePrefix} ${title} ${this.titleSuffix} <span class="rebuy-upsell__price">$${price}</span></h4>
          ${this.showDescription && description ? `<p class="rebuy-upsell__description">${description}</p>` : ''}
          ${inlineAction}
        </div>
        ${checkboxElement}
      </div>
    `;

    this.classList.add('rebuy-upsell--loaded');
    this.wireUpHandlers();
  }

  setupButtonHandler() {
    const button = this.querySelector('.rebuy-upsell__button');
    if (!button) return;

    button.addEventListener('click', async () => {
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      button.disabled = true;
      button.classList.add('rebuy-upsell__button--loading');
      const originalText = button.textContent;
      button.textContent = 'Adding...';

      try {
        await this.addUpsellToCart(variantId);
        button.textContent = 'Added!';
        button.classList.remove('rebuy-upsell__button--loading');
        button.classList.add('rebuy-upsell__button--success');

        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
          button.classList.remove('rebuy-upsell__button--success');
        }, 2000);
      } catch (error) {
        button.textContent = 'Error';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      }
    });
  }

  setupFormIntegration() {
    const checkbox = this.querySelector('input[type="checkbox"]');
    if (!checkbox) return;

    const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!productForm) return;

    productForm.addEventListener('submit', () => {
      if (checkbox.checked) {
        const variantId = checkbox.dataset.variantId;
        if (variantId) {
          this.pendingUpsellVariantId = variantId;
        }
      }
    });

    document.addEventListener('cart:refresh', () => this.addUpsellIfPending());
    this.interceptCartAdditions();
  }

  interceptCartAdditions() {
    const checkbox = this.querySelector('input[type="checkbox"]');
    if (!checkbox) return;

    const originalFetch = window.fetch;
    const self = this;

    window.fetch = function(url) {
      const fetchPromise = originalFetch.apply(this, arguments);

      if (url && (url.includes('/cart/add') || url.toString().includes('/cart/add'))) {
        fetchPromise.then((response) => {
          const clonedResponse = response.clone();
          clonedResponse.json().then(() => {
            if (checkbox.checked) {
              const variantId = checkbox.dataset.variantId;
              if (variantId) {
                self.addUpsellToCart(variantId);
              }
            }
          }).catch(() => {});
        }).catch(() => {});
      }

      return fetchPromise;
    };
  }

  async addUpsellToCart(variantId) {
    const item = {
      id: parseInt(variantId),
      quantity: 1,
    };

    if (this.dataset.prerendered !== 'true') {
      item.properties = {
        '_source': 'Rebuy',
        '_widget_id': this.customDataSourceId,
        '_attribution': 'Custom Rebuy Upsell',
      };
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items: [item] }),
    });

    if (!response.ok) {
      throw new Error('Failed to add to cart');
    }

    // Uncheck checkbox if in addon mode
    const checkbox = this.querySelector('input[type="checkbox"]');
    if (checkbox) checkbox.checked = false;

    // Dispatch event for cart drawer
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    return response.json();
  }

  truncateText(text, maxLength) {
    const stripped = text.replace(/<[^>]*>/g, '');
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength).trim() + '...';
  }
}

customElements.define('rebuy-upsell', RebuyUpsell);
